import { useState, useEffect, useContext, useCallback } from 'react';
import io from 'socket.io-client';
import ReactECharts from 'echarts-for-react';
import { AuthContext } from '../context/authContext';
import InfoButtonWithPopover from '../components/InfoButtonWithPopover';

const API = '/api';
const MAX_POINTS = 30;
const INTERVAL_MS = 3000;

const emptyHistory = () => ({
  times: [],
  heapUsed: [],
  rss: [],
  eventLoop: [],
  rps: [],
  latAvg: [],
  latP95: [],
  latP99: [],
  connActive: [],
  connIdle: [],
  cacheHit: [],
  usuarios: [],
  conexiones: [],
});

const push = (arr, v) => [...arr.slice(-(MAX_POINTS - 1)), v];

const ChartCard = ({ titulo, info, children }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-2">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 min-w-0">{titulo}</h3>
      {info && <InfoButtonWithPopover infoText={info} />}
    </div>
    {children}
  </div>
);

export default function DashboardMonitoreoPage() {
  const { token, role } = useContext(AuthContext);
  const [history, setHistory] = useState(emptyHistory);
  const [latest, setLatest] = useState(null);
  const [estado, setEstado] = useState('conectando');

  const appendSnapshot = useCallback((data) => {
    if (!data || !data.node || !data.http || !data.postgres) return;
    const t = new Date(data.timestamp).toLocaleTimeString('es-AR');
    setHistory((h) => ({
      times: push(h.times, t),
      heapUsed: push(h.heapUsed, data.node.heapUsedMb),
      rss: push(h.rss, data.node.rssMb),
      eventLoop: push(h.eventLoop, data.node.eventLoop?.lastDelayMs ?? 0),
      rps: push(h.rps, data.http.rps),
      latAvg: push(h.latAvg, data.http.avgMs),
      latP95: push(h.latP95, data.http.p95Ms),
      latP99: push(h.latP99, data.http.p99Ms),
      connActive: push(h.connActive, data.postgres.connections?.active ?? 0),
      connIdle: push(h.connIdle, data.postgres.connections?.idle ?? 0),
      cacheHit: push(h.cacheHit, data.postgres.database?.cacheHitRatio ?? 0),
      usuarios: push(h.usuarios, data.usuariosConectados?.unicos ?? 0),
      conexiones: push(h.conexiones, data.usuariosConectados?.conexiones ?? 0),
    }));
  }, []);

  useEffect(() => {
    if (role !== 'admin') return;
    let alive = true;

    const cargar = async () => {
      try {
        const res = await fetch(`${API}/metrics`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setLatest(data);
        appendSnapshot(data);
        setEstado('online');
      } catch (e) {
        if (alive) setEstado('error');
      }
    };

    cargar();
    const interval = setInterval(cargar, INTERVAL_MS);

    const socket = io({
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: {
        username: localStorage.getItem('user'),
        userId: localStorage.getItem('userId'),
      },
    });
    socket.on('metrics', (data) => {
      if (!alive) return;
      setLatest(data);
      appendSnapshot(data);
      setEstado('online');
    });

    return () => {
      alive = false;
      clearInterval(interval);
      socket.disconnect();
    };
  }, [role, token, appendSnapshot]);

  if (role !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        Acceso denegado. Solo administradores.
      </div>
    );
  }

  const fmtSeg = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const seg = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
  };

  const statusPieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: 0 },
    series: [
      {
        type: 'pie',
        radius: ['40%', '65%'],
        itemStyle: { borderRadius: 4 },
        data: [
          { name: '2xx', value: latest?.http?.countsByClass?.['2xx'] || 0, itemStyle: { color: '#91CC75' } },
          { name: '3xx', value: latest?.http?.countsByClass?.['3xx'] || 0, itemStyle: { color: '#FAC858' } },
          { name: '4xx', value: latest?.http?.countsByClass?.['4xx'] || 0, itemStyle: { color: '#EE6666' } },
          { name: '5xx', value: latest?.http?.countsByClass?.['5xx'] || 0, itemStyle: { color: '#C23531' } },
        ],
      },
    ],
  };

  const card = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm';

  const stat = (label, value, sub) => (
    <div className={card}>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center">
          <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Monitoreo del stack (React · Node · PostgreSQL)</h1>
          <InfoButtonWithPopover
            infoText="Dashboard en vivo del backend (server_obras_v2) y la base de datos. Se actualiza cada 3 segundos vía REST y Socket.IO. Las tarjetas muestran el valor actual y los gráficos el historial de los últimos 90 segundos (30 puntos). Solo visible para administradores."
          />
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${estado === 'online' ? 'bg-green-100 text-green-700' : estado === 'error' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {estado === 'online' ? 'En línea' : estado === 'error' ? 'Sin datos' : 'Conectando…'}
        </span>
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {stat('Uptime Node', fmtSeg(latest?.node?.uptimeSeconds || 0), 'tiempo de proceso activo')}
        {stat('Peticiones totales', latest?.http?.totalRequests ?? 0, 'desde el arranque')}
        {stat('RPS', latest?.http?.rps ?? '—', 'promedio en 10s')}
        {stat('Latencia p95', latest?.http?.p95Ms != null ? `${latest.http.p95Ms} ms` : '—', `promedio ${latest?.http?.avgMs ?? '—'} ms`)}
        {stat('Event loop', latest?.node?.eventLoop?.lastDelayMs != null ? `${latest.node.eventLoop.lastDelayMs} ms` : '—', 'delay del bucle de eventos')}
        {stat('Usuarios conectados', latest?.usuariosConectados?.unicos ?? 0, `${latest?.usuariosConectados?.conexiones ?? 0} conexiones Socket.IO`)}
        {stat('Conexiones PG pool', `${latest?.postgres?.connections?.poolTotal ?? 0}/${latest?.postgres?.connections?.poolMax ?? 0}`, `activas ${latest?.postgres?.connections?.active ?? 0} · inactivas ${latest?.postgres?.connections?.idle ?? 0}`)}
      </div>

      {/* Fila 0: usuarios conectados */}
      <ChartCard
        titulo="Usuarios conectados en tiempo real (Socket.IO)"
        info="Cantidad de usuarios con pestaña abierta del sistema conectados por Socket.IO: 'Usuarios únicos' cuenta cada usuario una sola vez sin importar cuántas pestañas tenga; 'Conexiones' cuenta cada socket (pestaña/ventana). Bajo el gráfico se listan los usuarios conectados en vivo con el número de conexiones (pestañas) de cada uno. Se actualiza cada 3 segundos."
      >
        <ReactECharts
          notMerge={false}
          lazyUpdate
          style={{ height: 260, width: '100%' }}
          option={{
            tooltip: { trigger: 'axis' },
            legend: { data: ['Usuarios únicos', 'Conexiones'], bottom: 0 },
            grid: { left: '4%', right: '4%', bottom: '14%', containLabel: true },
            xAxis: { type: 'category', boundaryGap: false, data: history.times },
            yAxis: { type: 'value', name: 'usuarios', minInterval: 1 },
            series: [
              {
                name: 'Usuarios únicos',
                type: 'line',
                smooth: true,
                data: history.usuarios,
                areaStyle: { opacity: 0.1 },
                itemStyle: { color: '#5470C6' },
              },
              { name: 'Conexiones', type: 'line', smooth: true, data: history.conexiones, itemStyle: { color: '#91CC75' } },
            ],
          }}
        />

        {/* Conectados por usuario: cada conexión tiene el nombre del user asociado */}
        {latest?.usuariosConectados?.usuarios?.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {latest.usuariosConectados.usuarios.map((u) => (
              <span
                key={u.usuario}
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300"
                title={`${u.socketCount} conexion${u.socketCount === 1 ? '' : 'es'}`}
              >
                <span className="h-2 w-2 rounded-full bg-green-500" />
                {u.usuario}
                <span className="text-indigo-400 dark:text-indigo-400/70">({u.socketCount})</span>
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">Sin usuarios conectados en este momento.</p>
        )}
      </ChartCard>

      {/* Fila 1: Node */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          titulo="Memoria del proceso Node"
          info="Memoria del proceso del backend (server_obras_v2): 'Heap usado' es la memoria en uso por el motor de JavaScript y 'RSS' la memoria física total del proceso (incluye código, buffers y stack). Un crecimiento sostenido sin mesetas suele indicar una fuga de memoria."
        >
          <ReactECharts
            notMerge={false}
            lazyUpdate
            style={{ height: 300, width: '100%' }}
            option={{
              tooltip: { trigger: 'axis' },
              legend: { data: ['Heap usado (MB)', 'RSS (MB)'], bottom: 0 },
              grid: { left: '4%', right: '4%', bottom: '14%', containLabel: true },
              xAxis: { type: 'category', boundaryGap: false, data: history.times },
              yAxis: { type: 'value', name: 'MB' },
              series: [
                { name: 'Heap usado (MB)', type: 'line', smooth: true, data: history.heapUsed, areaStyle: { opacity: 0.1 }, itemStyle: { color: '#5470C6' } },
                { name: 'RSS (MB)', type: 'line', smooth: true, data: history.rss, itemStyle: { color: '#91CC75' } },
              ],
            }}
          />
        </ChartCard>
        <ChartCard
          titulo="Event Loop Delay (bloqueo del bucle)"
          info="Tiempo (ms) que tarda el bucle de eventos de Node en responder. El proceso de Node es de un solo hilo: si este valor sube, el backend está bloqueado (consultas pesadas o CPU saturada) y deja de atender peticiones. Menos de 1 ms es saludable."
        >
          <ReactECharts
            notMerge={false}
            lazyUpdate
            style={{ height: 300, width: '100%' }}
            option={{
              tooltip: { trigger: 'axis' },
              grid: { left: '4%', right: '4%', bottom: '6%', containLabel: true },
              xAxis: { type: 'category', boundaryGap: false, data: history.times },
              yAxis: { type: 'value', name: 'ms', splitLine: { show: false } },
              series: [
                {
                  name: 'Delay (ms)',
                  type: 'line',
                  smooth: true,
                  data: history.eventLoop,
                  areaStyle: { opacity: 0.15 },
                  itemStyle: { color: '#EE6666' },
                },
              ],
            }}
          />
        </ChartCard>
      </div>

      {/* Fila 2: HTTP */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          titulo="Latencia HTTP (promedio, p95, p99) vs RPS"
          info="Latencia de las respuestas del backend: 'promedio', p95 y p99 (el 99% de las peticiones se responde en menos de esa cifra) y RPS (peticiones por segundo en el último lapso). Si p95/p99 se disparan mientras el promedio queda bajo, hay peticiones puntuales muy lentas (consultas pesadas o lock)."
        >
          <ReactECharts
            notMerge={false}
            lazyUpdate
            style={{ height: 300, width: '100%' }}
            option={{
              tooltip: { trigger: 'axis' },
              legend: { data: ['promedio (ms)', 'p95 (ms)', 'p99 (ms)', 'RPS'], bottom: 0 },
              grid: { left: '4%', right: '4%', bottom: '14%', containLabel: true },
              xAxis: { type: 'category', boundaryGap: false, data: history.times },
              yAxis: [
                { type: 'value', name: 'ms' },
                { type: 'value', name: 'RPS', splitLine: { show: false } },
              ],
              series: [
                { name: 'promedio (ms)', type: 'line', smooth: true, data: history.latAvg, itemStyle: { color: '#5470C6' } },
                { name: 'p95 (ms)', type: 'line', smooth: true, data: history.latP95, itemStyle: { color: '#FAC858' } },
                { name: 'p99 (ms)', type: 'line', smooth: true, data: history.latP99, itemStyle: { color: '#EE6666' } },
                { name: 'RPS', type: 'line', smooth: true, yAxisIndex: 1, data: history.rps, itemStyle: { color: '#91CC75' } },
              ],
            }}
          />
        </ChartCard>
        <ChartCard
          titulo="Distribución de códigos de estado"
          info="Proporción de respuestas HTTP del backend por clase de código, acumulada desde el arranque del proceso: 2xx = correctas, 3xx = redirecciones, 4xx = errores del cliente (petición inválida, no autorizado), 5xx = errores del servidor. Un aumento de 5xx indica problemas del backend."
        >
          <ReactECharts notMerge={false} lazyUpdate style={{ height: 300, width: '100%' }} option={statusPieOption} />
        </ChartCard>
      </div>

      {/* Fila 3: PostgreSQL */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          titulo="Conexiones PostgreSQL (activas / inactivas)"
          info="Conexiones al pool de PostgreSQL: 'Activas' ejecutan una consulta en ese momento e 'Inactivas' están en el pool sin uso (a la espera de una petición). La línea punteada marca el límite del pool (poolMax). Acercarse al límite satura las consultas (esperas por conexión)."
        >
          <ReactECharts
            notMerge={false}
            lazyUpdate
            style={{ height: 300, width: '100%' }}
            option={{
              tooltip: { trigger: 'axis' },
              legend: { data: ['Activas', 'Inactivas'], bottom: 0 },
              grid: { left: '4%', right: '4%', bottom: '14%', containLabel: true },
              xAxis: { type: 'category', boundaryGap: false, data: history.times },
              yAxis: { type: 'value', name: 'conexiones' },
              series: [
                {
                  name: 'Activas',
                  type: 'line',
                  smooth: true,
                  data: history.connActive,
                  areaStyle: { opacity: 0.1 },
                  itemStyle: { color: '#5470C6' },
                  markLine: {
                    symbol: 'none',
                    data: [{ yAxis: latest?.postgres?.connections?.poolMax ?? 20, name: 'Pool max' }],
                    lineStyle: { type: 'dashed', color: '#C23531' },
                    label: { formatter: 'pool max' },
                  },
                },
                { name: 'Inactivas', type: 'line', smooth: true, data: history.connIdle, itemStyle: { color: '#91CC75' } },
              ],
            }}
          />
        </ChartCard>
        <ChartCard
          titulo="Cache hit ratio (buffer cache)"
          info="Porcentaje de lecturas de bloques que PostgreSQL responde desde su memoria compartida (buffer cache) sin tocar el disco. Es la métrica más importante de rendimiento de la BD: por encima del 95-99% es saludable; valores bajos indican buffers pequeños o disco lento."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ReactECharts
              notMerge={false}
              lazyUpdate
              style={{ height: 240, width: '100%' }}
              option={{
                series: [
                  {
                    type: 'gauge',
                    startAngle: 210,
                    endAngle: -30,
                    min: 0,
                    max: 100,
                    progress: { show: true, width: 12 },
                    axisLine: { lineStyle: { width: 12 } },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    axisLabel: { show: false },
                    detail: { valueAnimation: true, formatter: '{value}%', fontSize: 20, offsetCenter: [0, 0] },
                    data: [{ value: latest?.postgres?.database?.cacheHitRatio ?? 0, name: 'Cache hit' }],
                  },
                ],
              }}
            />
            <div className="space-y-2">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="block text-gray-500 dark:text-gray-400">Base de datos</span>
                {latest?.postgres?.database?.db_size ?? '—'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="block text-gray-500 dark:text-gray-400">shared_buffers</span>
                {latest?.postgres?.database?.shared_buffers ?? '—'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="block text-gray-500 dark:text-gray-400">max_connections</span>
                {latest?.postgres?.database?.max_connections ?? '—'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className="block text-gray-500 dark:text-gray-400">Transacciones</span>
                commit {latest?.postgres?.database?.xact_commit ?? 0} / rollback {latest?.postgres?.database?.xact_rollback ?? 0}
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Consultas lentas */}
      <div className={card}>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 min-w-0">
            {latest?.postgres?.topQueriesFuente === 'pg_stat_activity'
              ? 'Consultas activas / demoradas en vivo (pg_stat_activity)'
              : 'Top 5 consultas más costosas (pg_stat_statements)'}
          </h2>
          <InfoButtonWithPopover
            infoText={
              latest?.postgres?.topQueriesFuente === 'pg_stat_activity'
                ? "Fotografía de las consultas que se están ejecutando ahora o quedaron 'idle in transaction' (retienen transacción y potencialmente locks), ordenadas por duración. Se actualiza cada 3 segundos. Como es una vista del momento y no histórico, si no hay actividad la tabla puede quedar vacía."
                : 'Las 5 consultas con mayor tiempo de ejecución total acumulado desde que arrancó PostgreSQL (pg_stat_statements). Calls = veces ejecutadas, Media/Máx/Total en milisegundos. Sirve para detectar consultas pesadas o con muchas ejecuciones de forma histórica.'
            }
          />
        </div>
        {(latest?.postgres?.topQueries || []).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No hay consultas activas en este momento ni datos de la extensión{' '}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">pg_stat_statements</code> (requiere{' '}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">shared_preload_libraries = 'pg_stat_statements'</code> y crear la vista).
            <br />
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Diagnóstico: extensión{' '}
              {latest?.postgres?.pgStatStatements?.instalada ? 'instalada' : 'NO instalada en esta base'}
              {latest?.postgres?.pgStatStatements?.error ? ` · ${latest.postgres.pgStatStatements.error}` : ''}
            </span>
          </p>
        ) : latest?.postgres?.topQueriesFuente === 'pg_stat_activity' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3">Consulta</th>
                  <th className="py-2 pr-3">Usuario</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 pr-3">Duración (s)</th>
                  <th className="py-2">PID</th>
                </tr>
              </thead>
              <tbody>
                {latest.postgres.topQueries.map((q, i) => (
                  <tr key={q.pid || i} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">{q.query}</td>
                    <td className="py-2 pr-3">{q.usename}{q.datname ? ` · ${q.datname}` : ''}</td>
                    <td className="py-2 pr-3">{q.state}</td>
                    <td className="py-2 pr-3">{q.duracion_seg}</td>
                    <td className="py-2">{q.pid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3">Consulta</th>
                  <th className="py-2 pr-3">Calls</th>
                  <th className="py-2 pr-3">Media (ms)</th>
                  <th className="py-2 pr-3">Máx (ms)</th>
                  <th className="py-2">Total (ms)</th>
                </tr>
              </thead>
              <tbody>
                {latest.postgres.topQueries.map((q, i) => (
                  <tr key={q.queryid || i} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">{q.query}</td>
                    <td className="py-2 pr-3">{q.calls}</td>
                    <td className="py-2 pr-3">{q.mean_ms}</td>
                    <td className="py-2 pr-3">{q.max_ms}</td>
                    <td className="py-2">{q.total_ms}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}